import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { useSearchParams } from "react-router-dom";
import SearchInput from "../../components/Search/SearchInput";
import WarehouseAssignTab from "../Warehouse/HomePage/WarehouseAssignTab";
import PendingDispatchTab from "../Warehouse/HomePage/PendingDispatchTab";
import DispatchedHistoryTab from "../Warehouse/HomePage/DispatchedHistoryTab";
import PartiallyDispatchedTab from "../Warehouse/HomePage/PartiallyDispatchedTab";
import DispatchModal from "../Warehouse/HomePage/DispatchModal";
import WarehouseAssignModal from "../Warehouse/HomePage/WarehouseAssignModal";
import ProductReplacementModal from "./ProductReplacementModal";
import {
  fetchPendingAssignment,
  fetchPendingDispatch,
  fetchPartiallyDispatched,
  fetchFullyDispatched,
} from "../../store/slices/orderSlice";
import { fetchUserWarehouseStock } from "../../store/slices/floorInwardSlice";
import { getProductSn } from "../../store/slices/productsSnSlice";

const WarehouseHomeTab: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const role = useSelector((state: RootState) => state.auth.role);
  const { paginations } = useSelector((state: RootState) => state.order);
  const { warehouses } = useSelector((state: RootState) => state.warehouse);
  const products = useSelector((state: RootState) => state.product.products);

  const isDispatchLevel1 = role === "admin" || role === "dispatch-lv1";
  const activeTab =
    searchParams.get("tab") || (isDispatchLevel1 ? "assignment" : "pending");

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [replacementModalOpen, setReplacementModalOpen] = useState(false);
  const replacementButtonRef = useRef<HTMLButtonElement>(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedAssignOrder, setSelectedAssignOrder] = useState<any>(null);

  const openDispatchModal = useCallback((orderId: string) => {
    setSelectedOrderId(orderId);
    setModalOpen(true);
  }, []);

  const openAssignModal = useCallback((order: any) => {
    setSelectedAssignOrder(order);
    setAssignModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedOrderId(null);
  };

  const closeReplacementModal = () => {
    setReplacementModalOpen(false);
    replacementButtonRef.current?.focus();
  };

  const refreshAfterReplacement = () => {
    const params = {
      page: 1,
      search: searchTerm,
      startDate: dateRange.from,
      endDate: dateRange.to,
    };
    dispatch(fetchPendingDispatch(params));
    dispatch(fetchFullyDispatched(params));
    if (isDispatchLevel1) {
      dispatch(fetchPartiallyDispatched(params));
    }

    dispatch(fetchUserWarehouseStock());
    const productIds = products.map((product) => product._id);
    if (productIds.length) {
      dispatch(getProductSn({ productIds }));
    }
  };

  const selectedOrder = useSelector(
    (state: RootState) =>
      state.order.pendingDispatch.find((o) => o._id === selectedOrderId) ||
      state.order.partiallyDispatched.find((o) => o._id === selectedOrderId),
  );

  const warehouseOptions = useMemo(
    () => warehouses.map((w: any) => ({ value: w._id, label: w.name })),
    [warehouses],
  );

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  useEffect(() => {
    const fetchAllData = async () => {
      const params = {
        page: 1,
        search: searchTerm,
        startDate: dateRange.from,
        endDate: dateRange.to,
      };

      const calls = [];

      calls.push(dispatch(fetchPendingDispatch(params)));
      calls.push(dispatch(fetchFullyDispatched(params)));

      if (isDispatchLevel1) {
        calls.push(dispatch(fetchPendingAssignment(params)));
        calls.push(dispatch(fetchPartiallyDispatched(params)));
      }

      await Promise.all(calls);
    };

    fetchAllData();
  }, [dispatch, searchTerm, dateRange, isDispatchLevel1]);

  const tabs = useMemo(() => {
    const allTabs = [
      {
        id: "assignment",
        label: "Pending Assignment",
        color: "orange",
        count: paginations.pendingAssignment?.total || 0,
        permission: isDispatchLevel1,
      },
      {
        id: "pending",
        label: "Pending Dispatch",
        color: "yellow",
        count: paginations.pendingDispatch?.total || 0,
        permission: true,
      },
      {
        id: "partial",
        label: "Partially Dispatched",
        color: "blue",
        count: paginations.partiallyDispatched?.total || 0,
        permission: isDispatchLevel1,
      },
      {
        id: "history",
        label: "Dispatch History",
        color: "green",
        count: paginations.fullyDispatched?.totalCount || 0,
        permission: true,
      },
    ];
    return allTabs.filter((tab) => tab.permission);
  }, [paginations, isDispatchLevel1]);

  const getTabColorClass = (
    tabId: string,
    color: string,
    isActive: boolean,
  ) => {
    if (!isActive)
      return "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300";
    const colors: Record<string, string> = {
      orange: "border-orange-500 text-orange-600",
      yellow: "border-yellow-500 text-yellow-600",
      blue: "border-blue-500 text-blue-600",
      green: "border-green-500 text-green-600",
    };
    return colors[color] || "";
  };

  const getBadgeColorClass = (
    tabId: string,
    color: string,
    isActive: boolean,
  ) => {
    if (!isActive) return "bg-slate-100 text-slate-600";
    const colors: Record<string, string> = {
      orange: "bg-orange-100 text-orange-800",
      yellow: "bg-yellow-100 text-yellow-800",
      blue: "bg-blue-100 text-blue-800",
      green: "bg-green-100 text-green-800",
    };
    return colors[color] || "";
  };

  return (
    <div className="min-h-screen bg-slate-50 font-inter antialiased">
      <main className="px-4 sm:px-6 py-8 space-y-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4 items-end">
          {/* Search Bar*/}
          <div className="w-full">
            <SearchInput
              placeholder="Order # or Customer..."
              className="w-full p-1.5 border rounded-md focus:outline-none text-xs bg-white"
              onSearch={setSearchTerm}
            />
          </div>
          {/* Date Inputs Grouped Together */}
          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold text-slate-500 uppercase ml-1">
                From
              </label>
              <input
                type="date"
                className="p-2 border rounded-md focus:outline-none text-xs bg-white text-slate-600 w-36"
                value={dateRange.from}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, from: e.target.value }))
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold text-slate-500 uppercase ml-1">
                To
              </label>
              <input
                type="date"
                className="p-2 border rounded-md focus:outline-none text-xs bg-white text-slate-600 w-36"
                value={dateRange.to}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, to: e.target.value }))
                }
              />
            </div>

            {/* Clear Button - only shows when filters are active */}
            {(dateRange.from || dateRange.to) && (
              <button
                onClick={() => setDateRange({ from: "", to: "" })}
                className="h-9 px-3 text-[10px] font-bold text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors mt-5"
              >
                CLEAR
              </button>
            )}
          </div>
          <button
            ref={replacementButtonRef}
            type="button"
            onClick={() => setReplacementModalOpen(true)}
            className="h-9 shrink-0 rounded-md bg-blue-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
          >
            Replace Product
          </button>
        </div>

        <div className="flex space-x-1 border-b overflow-x-auto no-scrollbar bg-white rounded-t-lg px-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-3 border-b-2 transition-colors whitespace-nowrap flex items-center text-sm font-medium ${getTabColorClass(tab.id, tab.color, activeTab === tab.id)}`}
            >
              {tab.label}
              <span
                className={`ml-2 text-xs px-2 py-0.5 rounded-full ${getBadgeColorClass(tab.id, tab.color, activeTab === tab.id)}`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          {activeTab === "assignment" && isDispatchLevel1 && (
            <WarehouseAssignTab searchTerm={searchTerm} dateRange={dateRange} />
          )}
          {activeTab === "pending" && (
            <PendingDispatchTab
              onDispatchClick={openDispatchModal}
              searchTerm={searchTerm}
              dateRange={dateRange}
            />
          )}

          {activeTab === "partial" && (
            <PartiallyDispatchedTab
              onAssignClick={openAssignModal}
              onDispatchClick={openDispatchModal}
              searchTerm={searchTerm}
              dateRange={dateRange}
            />
          )}

          {activeTab === "history" && (
            <DispatchedHistoryTab
              searchTerm={searchTerm}
              dateRange={dateRange}
            />
          )}
        </section>
      </main>

      <DispatchModal
        selectedOrder={selectedOrder}
        isOpen={modalOpen}
        onClose={closeModal}
      />

      <WarehouseAssignModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        order={selectedAssignOrder}
        warehouseOptions={warehouseOptions}
      />

      <ProductReplacementModal
        isOpen={replacementModalOpen}
        onClose={closeReplacementModal}
        onSuccess={refreshAfterReplacement}
      />
    </div>
  );
};

export default WarehouseHomeTab;
